// HTTP request core: authed `request` (CSRF + cookies + error normalization) and
// anonymous `publicRequest` (no cookie, no CSRF). Extracted from api/client.ts
// (verbatim). `request` is public; `publicRequest` is used by api.ts.
import { API_BASE } from './constants';
import { parseError } from './errors';
import { ensureCsrf, clearCsrf } from './csrf';

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOpts {
  method?: string;
  body?: unknown;
  query?: Record<string, string | undefined>;
  _retried?: boolean;
}

function buildQuery(q?: Record<string, string | undefined>): string {
  if (!q) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined) params.set(k, v);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (UNSAFE.has(method)) headers['X-CSRF-Token'] = await ensureCsrf();

  const res = await fetch(API_BASE + path + buildQuery(opts.query), {
    method,
    headers,
    body,
    credentials: 'include',
  });

  if (!res.ok) {
    const err = await parseError(res);
    if (err.status === 403 && UNSAFE.has(method) && !opts._retried && !err.code && /csrf/i.test(err.message)) {
      clearCsrf();
      return request<T>(path, { ...opts, _retried: true });
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ── D1 PUBLIC share surface (root /share/*, OUTSIDE /api/v1) ─────────────────────
// The anonymous viewer holds no account: send NO session cookie and NO CSRF token
// (the capability is the high-entropy token in the path, re-validated server-side
// every call). credentials:'omit' also means a logged-in user opening a share link
// does NOT leak their cookie to this account-agnostic surface. Errors normalize
// through the same parseError (a uniform 404 → a generic ApiError the page maps to
// "this link isn't available").
export async function publicRequest<T>(
  path: string,
  opts: { method?: string; body?: unknown; query?: Record<string, string | undefined> } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(path + buildQuery(opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body,
    credentials: 'omit',
  });
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
